import React, { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import Form from '@lib/wrappers/Form';
import PreferencesPageAddressViewer from './PreferencesPageAddressViewer';
import PreferencesPageControls from './PreferencesPageControls';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../../ssap/notifications/ValidationErrorMessage';
import Tooltip from '../../common/tooltip/Tooltip';
import LanguageMenu from '@lib/inputs/LanguageMenu';
import Button from '@components/common/button/Button';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';

// Hooks
import { useLocation } from '@hooks/useLocation';

// Constants
import { commonModalTypes as ModalTypes } from '@constants/common/modals/commonModalTypes';
import { configs } from '@constants/configs/configs';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { preventerTools } from '@utils/tools/preventerTools';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { navigateByRole } from '@utils/tools/landingPage';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import { INPUT_TYPES as BUTTON_INPUT_TYPES } from '@components/common/form/constants/fieldTypes';

//Utils
import { omit } from '@utils/miscellaneous/objectHelpers';
import PreferencesConfirmation from './PreferencesConfirmation';
import { addEvent } from '@utils/tools/GTMUtils';
import {
    GTM_EVENT_CATEGORIES,
    GTM_EVENT_ACTIONS,
    GTM_EVENT_LABELS,
    getGTMEventLabel,
} from '@constants/common/gtmEventConstants';
import {
    communicationPreferences,
    communicationPreferencesReviewTypes,
    communicationPreferencesTypes,
    verificationStatusFields,
} from '@constants/individualPortal/preferences/preferenceTypes';
import useAppDispatch from '@hooks/useAppDispatch';
import { decrementCommunicationPreferenceConfirmationCount } from '@actions/common/actionCreators';
import { removeKeys } from '@utils/miscellaneous/cloneObject';
import { logValidationError } from '@utils/tools/ErrorLoggers';

const CommunicationPreferencesPage = props => {
    const COMMUNICATION_METHOD_SUCCESS_ID = 'preferences_page.preferences_save_success';
    const EXIT_CLICK = 'onFormExit';
    const MAIL_ADDRESS_SUCCESS_ID = 'preferences_page.mailing_address_save_success';
    const PREFERENCES_FORM = 'preferencesForm';
    const SUBMIT_CLICK = 'onFormSubmit';
    const PHONE_NUMBER_PLUS_ONE_REGEX = /^1[+-]/;

    const navigate = useNavigate();

    const {
        actions: { fetchExchangeDetails, fetchLanguages, fetchPreferences, savePreferences, showModal },
        data,
        form,
        isDashboardLocation,
        isState,
        methods,
        switchBackLink,
        updateData,
        resetCommunicationPrefCount = () => {},
        environment: {
            links: { PRIVACY_POLICY, PHONE },
            user: userData,
            exchangeName,
            impersonationDetails,
            stateCode,
        },
    } = props;

    const {
        emailAddress,
        emailMe,
        emailVerified,
        mailingAddressUpdateAllowed,
        mailingAddress,
        paperlessNotices,
        noticeOptOut,
        phoneNumber,
        phoneVerified,
        preferredSpokenLanguage,
        preferredWrittenLanguage,
        paperless1095,
        showNoticeOptOut,
        smsUnsubscribed,
        smsVerified,
        textMe,
        userRecordExists,
        phoneConfirmationNeeded,
        emailConfirmationNeeded,
        addressConfirmationNeeded,
        paperlessConfirmationNeeded,
        languageConfirmationNeeded,
        marketCommunConfirmationNeeded,
        atRiskDate,
        outdatedMailingAddressReturnCount,
    } = data;

    const { householdId } = impersonationDetails || {};

    const [exchangePhone, setExchangePhone] = useState(PHONE);
    const [languages, setLanguages] = useState({ languageSpokenList: [], languageWrittenList: [] });

    const dispatch = useAppDispatch();

    const getLanguages = async () => {
        const response = await fetchLanguages();
        if (response) {
            setLanguages(response);
        }
    };

    useEffect(() => {
        getLanguages();
        if (mailingAddress && mailingAddress.streetAddress1 === null && mailingAddressUpdateAllowed) {
            showModal({
                modalType: dashboardModalTypes.MAILING_ADDRESS_UPDATING_MODAL,
                modalData: {
                    actionNames: {
                        EXIT_CLICK,
                        SUBMIT_CLICK,
                    },
                    data,
                    handleAddressSave,
                    isDashboardLocation: false,
                    mailingAddress,
                },
            });
        }
    }, []);

    const getExchangeDetails = async () => {
        const response = await fetchExchangeDetails();
        const { success, payload } = response;
        if (success) {
            const { exchangeSmsPhoneNumber } = payload;
            setExchangePhone(exchangeSmsPhoneNumber);
        }
    };
    useEffect(() => {
        if (smsUnsubscribed) {
            getExchangeDetails();
        }
    }, [smsUnsubscribed]);

    const intlApi = useIntl();
    const { fromCAP } = useLocation();

    const { isFormSubmitted, isFormOnTopLevelValid } = preventerTools.provideObject(form[PREFERENCES_FORM]);

    const onFieldChange = async (...args) => {
        const [newData, , , [name, value]] = args;

        const saveResult = await updatePreferences({ ...data, [name]: value });

        if (saveResult?.success) {
            const key = {
                noticeOptOut: 'marketCommunConfirmationNeeded',
                paperlessNotices: 'paperlessConfirmationNeeded',
                paperless1095: 'paperlessConfirmationNeeded',
            };
            const updatedDetails = {};

            if (key[name]) {
                dispatch(decrementCommunicationPreferenceConfirmationCount());
                updatedDetails[key[name]] = false;
                resetCommunicationPrefCount?.();
            }

            updateData(data => ({
                ...data,
                ...newData,
                ...updatedDetails,
                textMe:
                    (name === 'phoneNumber' && !value) || (name === 'textMe' && !data.phoneNumber)
                        ? false
                        : (newData.textMe ?? data.textMe),
                emailMe:
                    (name === 'emailAddress' && !value) || (name === 'emailMe' && !data.emailAddress)
                        ? false
                        : (newData.emailMe ?? data.emailMe),
            }));
        } else {
            handleFailureModal();
        }
    };

    const onLanguageChange = async (name, value) => {
        if (data[name] !== value) {
            const saveResult = await updatePreferences({ ...data, [name]: value });
            if (saveResult?.success) {
                dispatch(decrementCommunicationPreferenceConfirmationCount());
                updateData(data => ({
                    ...data,
                    [name]: value,
                    languageConfirmationNeeded: false,
                }));
                resetCommunicationPrefCount?.();
            } else {
                handleFailureModal();
            }
        }
    };

    const updatePreferences = (updatedData, successMessage = '') => {
        return savePreferences(updatedData).then(res => {
            if (res?.success && !configs.ENV.DEVELOPMENT) {
                const keysToExclude = [
                    ...Object.values(communicationPreferencesReviewTypes),
                    ...verificationStatusFields,
                ];
                const filteredPayload = removeKeys(res.payload, keysToExclude);

                updateData(prev => ({
                    ...prev,
                    ...filteredPayload,
                    noUpdateOfCommPrefOnEmailAddressError: true,
                }));
            }

            return Promise.resolve({
                errorMessage: res?.message,
                success: res?.success,
                successMessage,
            });
        });
    };

    const handleFailureModal = () => {
        showModal({
            modalType: dashboardModalTypes.FAILURE_MODAL,
            modalData: {},
        });
    };

    const showSaveResultModal = (callback, saveResult) => {
        showModal({
            modalType: ModalTypes.SAVE_RESULT_MODAL,
            modalData: {
                callback,
                saveResult,
            },
        });
    };

    const navigateToCAP = () => (global.location = switchBackLink);

    const navigateToRoleLandingPage = () =>
        navigateByRole(userData.role, userData.landingPage, navigate);

    const handlePreferencesSubmit = async dataExceptAddress => {
        const saveResult = await updatePreferences({ ...data, ...dataExceptAddress }, COMMUNICATION_METHOD_SUCCESS_ID);
        if (saveResult?.success) {
            showSaveResultModal(fromCAP ? navigateToCAP : navigateToRoleLandingPage, saveResult);
        } else {
            handleFailureModal();
        }
    };

    const handleAddressSave = async (data, callback) => {
        const saveResult = await updatePreferences(data, MAIL_ADDRESS_SUCCESS_ID);
        if (saveResult?.success) {
            dispatch(decrementCommunicationPreferenceConfirmationCount());
            updateData(prev => ({
                ...prev,
                addressConfirmationNeeded: false,
            }));
            resetCommunicationPrefCount?.();
            showSaveResultModal(callback, saveResult);
        } else {
            handleFailureModal();
        }
    };

    const handleClick = () => {
        fromCAP ? navigateToCAP() : navigateToRoleLandingPage();
    };

    /**
     * Validates required fields and logs validation errors if any are missing
     * @param fields - Record of field names to their values
     * @param handlerName - Name of the handler function for logging
     * @returns true if all fields are present, false otherwise
     */
    const validateRequiredFields = (fields: Record<string, unknown>, handlerName: string): boolean => {
        const missingFields = Object.entries(fields)
            .filter(([_, value]) => !value)
            .map(([key]) => key);

        if (missingFields.length > 0) {
            logValidationError(handlerName, missingFields, 'CommunicationPreferencesPage');
            return false;
        }
        return true;
    };

    const handleSwitchingToAddressUpdate = () => {
        if (!validateRequiredFields({ data, mailingAddress }, 'handleSwitchingToAddressUpdate')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesAddressUpdateClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.ADDRESS_UPDATE_CLICKED,
            eventLabel: GTM_EVENT_LABELS.OPEN_MAILING_ADDRESS_UPDATE_MODAL,
        });
        showModal({
            modalType: dashboardModalTypes.MAILING_ADDRESS_UPDATING_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data,
                handleAddressSave,
                isDashboardLocation,
                mailingAddress,
            },
        });
    };

    const handleContactUpdate = async (_, name, value) => {
        const response = await fetchPreferences();
        const { success, payload } = response || {};

        const contactUpdateConfirmationKey =
            name === communicationPreferences.emailAddress
                ? communicationPreferencesReviewTypes.EMAIL
                : communicationPreferencesReviewTypes.PHONE_NUMBER;

        const updatedContactDetails = {};

        if (name !== communicationPreferences.phoneNumber || phoneVerified) {
            updatedContactDetails[contactUpdateConfirmationKey] = false;
            dispatch(decrementCommunicationPreferenceConfirmationCount());
        }

        if (Object.keys(updatedContactDetails).length !== 0) resetCommunicationPrefCount?.();

        if (success) {
            const {
                emailAddress,
                emailMe,
                mailingAddress,
                mailingAddress: { state },
                phoneNumber,
                preferredSpokenLanguage,
                preferredWrittenLanguage,
                textMe,
            } = payload;

            // Only strip confirmation flags here; verification/status fields from fetchPreferences
            // are authoritative (unlike savePreferences, which may echo stale values).
            const filteredPayload = removeKeys(payload, Object.values(communicationPreferencesReviewTypes));

            updateData(prev => ({
                ...prev,
                ...filteredPayload,
                ...updatedContactDetails,
                [name]: value,
                textMe: phoneNumber ? textMe : false,
                emailMe: emailAddress ? emailMe : false,
                preferredSpokenLanguage: preferredSpokenLanguage ? preferredSpokenLanguage : 'English',
                preferredWrittenLanguage: preferredWrittenLanguage ? preferredWrittenLanguage : 'English',
                mailingAddress: {
                    ...mailingAddress,
                    ...(state === null ? { state: stateCode } : {}),
                },
                noUpdateOfCommPrefOnEmailAddressError: true,
            }));
        }
    };

    const handleChangeEmailModal = () => {
        if (!validateRequiredFields({ data, emailAddress }, 'handleChangeEmailModal')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesChangeEmailClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.CHANGE_EMAIL_CLICKED,
            eventLabel: getGTMEventLabel.emailAction(!!emailAddress),
        });
        showModal({
            modalType: dashboardModalTypes.CHANGE_EMAIL_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'emailAddress',
                type: INPUT_TYPES.REGULAR_INPUT,
                validationType: VALIDATION_TYPES.EMAIL,
                updateData: handleContactUpdate,
                value: emailAddress,
            },
        });
    };

    const handleAddEmailModal = () => {
        addEvent({
            event: 'communicationPreferencesAddEmailClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.ADD_EMAIL_CLICKED,
            eventLabel: GTM_EVENT_LABELS.ADD_NEW_EMAIL,
        });
        showModal({
            modalType: dashboardModalTypes.CHANGE_EMAIL_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'emailAddress',
                type: INPUT_TYPES.REGULAR_INPUT,
                validationType: VALIDATION_TYPES.EMAIL,
                updateData: handleContactUpdate,
                value: emailAddress,
            },
        });
    };

    const handleVerifyEmailModal = () => {
        if (!validateRequiredFields({ data, emailAddress }, 'handleVerifyEmailModal')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesVerifyEmailClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.VERIFY_EMAIL_CLICKED,
            eventLabel: GTM_EVENT_LABELS.OPEN_EMAIL_VERIFICATION_MODAL,
        });
        showModal({
            modalType: dashboardModalTypes.VERIFY_EMAIL_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'emailAddress',
                validationType: VALIDATION_TYPES.EMAIL,
                updateData: handleContactUpdate,
                value: emailAddress,
            },
        });
    };

    const handleChangePhoneModal = () => {
        if (!validateRequiredFields({ data }, 'handleChangePhoneModal')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesChangePhoneClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.CHANGE_PHONE_CLICKED,
            eventLabel: getGTMEventLabel.phoneAction(!!phoneNumber),
        });
        showModal({
            modalType: dashboardModalTypes.CHANGE_PHONE_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'phoneNumber',
                type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
                value: phoneNumber,
            },
        });
    };

    const handleVerifyPhoneModal = () => {
        if (!validateRequiredFields({ data, phoneNumber }, 'handleVerifyPhoneModal')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesVerifyPhoneClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.VERIFY_PHONE_CLICKED,
            eventLabel: GTM_EVENT_LABELS.OPEN_PHONE_VERIFICATION_MODAL,
        });
        showModal({
            modalType: dashboardModalTypes.VERIFY_PHONE_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'phoneNumber',
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
                value: phoneNumber,
            },
        });
    };

    const handleVerifySMSModal = () => {
        if (!validateRequiredFields({ data, phoneNumber }, 'handleVerifySMSModal')) {
            return;
        }

        addEvent({
            event: 'communicationPreferencesVerifySMSClick',
            eventCategory: GTM_EVENT_CATEGORIES.COMMUNICATION_PREFERENCES,
            eventAction: GTM_EVENT_ACTIONS.VERIFY_SMS_CLICKED,
            eventLabel: GTM_EVENT_LABELS.OPEN_SMS_VERIFICATION_MODAL,
        });
        showModal({
            modalType: dashboardModalTypes.VERIFY_PHONE_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'phoneNumber',
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
                value: phoneNumber,
                isSMSValidation: true,
            },
        });
    };

    const handleMailingAddressExtension = () => {
        methods[PREFERENCES_FORM][EXIT_CLICK]();
    };

    const getLinkToDashboard = content => <Link to={configs.routes.INDPORTAL}>{content}</Link>;

    const checkTopLevelValidity = () => {
        return (!paperlessNotices && !paperless1095) || emailMe || textMe;
    };

    const getLanguageFromList = (languageList, language) => {
        const found = languageList.find(lang => lang.label === language);
        return found ? `ssap.input.option.language_${found.code}` : 'common.no_text';
    };

    const isVerifyPhoneEnabled = !!phoneNumber && !smsUnsubscribed && !textMe && !phoneVerified;

    return (
        <Fragment>
            <div className="height-7" />

            <Form
                {...{
                    data: omit(data, 'mailingAddress'),
                    formName: PREFERENCES_FORM,
                    onLocalFormExit: handleSwitchingToAddressUpdate,
                    onLocalFormSubmit: handlePreferencesSubmit,
                    topLevelValidity: checkTopLevelValidity(),
                }}
            >
                <div className="gi-panel" data-testid="preferences-section-communication">
                    <h3 className="mb-4" data-testid="preferences-heading-communication">
                        <FormattedMessage id="ssap.page_title.communication_prefernces" values={{ exchangeName }} />
                    </h3>
                    <p data-testid="preferences-intro-communication">
                        <FormattedMessage id="preferences_page.warnings.asterisk" values={{ exchangeName }} />
                    </p>

                    <div className="contact-section my-4" data-testid="preferences-subsection-phone">
                        <div className="contact-section__label p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.phone_number" />
                            </span>
                            <Tooltip
                                id="preferences_phone_number"
                                index="preferences_phone_number"
                                content="preferences.tooltip.phone_number"
                                contentReplacement={{
                                    br: <br />,
                                }}
                                icon="question-circle"
                                iconLabel={intlApi.formatMessage({ id: 'ssap.input.label.phone_number' })}
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="contact-section__info">
                            <div className="contact-section__content gap-4">
                                <ConditionalRendering isTrue={!!phoneNumber}>
                                    <span className="text-nowrap" data-testid="preferences-phone-number">
                                        {phoneNumberTools.getAdjustedPhoneNumber(phoneNumber)}
                                    </span>
                                    <span
                                        data-testid="preferences-phone-verification-status"
                                        className={`app-status-indicator font-weight-normal badge py-2 px-3 ${phoneVerified ? 'category_status--completed' : 'category_status--pending'}`}
                                    >
                                        {phoneVerified ? (
                                            <>
                                                <FontAwesomeIcon icon="check-circle" className="mr-2" />
                                                <FormattedMessage id="common.status.verified" />
                                            </>
                                        ) : (
                                            intlApi
                                                .formatMessage({ id: 'common.status.unverified', defaultMessage: ' ' })
                                                .trim()
                                        )}
                                    </span>
                                </ConditionalRendering>
                                <div className="d-flex flex-wrap gap-2">
                                    <Button
                                        id="preferences_phone_number_button"
                                        dataTestId={
                                            phoneNumber
                                                ? 'preferences-change-phone-button'
                                                : 'preferences-add-phone-button'
                                        }
                                        name="phoneNumber"
                                        classes={['usa-button', 'gi-button__outline']}
                                        outerClass="flex-shrink-0"
                                        type={BUTTON_INPUT_TYPES.BUTTON}
                                        btnText={`ssap.button.${phoneNumber ? 'change_phone' : 'add_phone'}`}
                                        icon="edit"
                                        iconPlacement="START"
                                        handleClick={handleChangePhoneModal}
                                    />
                                    <ConditionalRendering isTrue={isVerifyPhoneEnabled}>
                                        <Button
                                            id="preferences_verify_phone_button"
                                            dataTestId="preferences-verify-phone-button"
                                            name="phoneNumber"
                                            classes={['usa-button', 'gi-button__outline']}
                                            outerClass="flex-shrink-0"
                                            type={BUTTON_INPUT_TYPES.BUTTON}
                                            btnText="ssap.button.verify_phone"
                                            handleClick={handleVerifyPhoneModal}
                                        />
                                    </ConditionalRendering>
                                </div>
                            </div>
                            <ConditionalRendering isTrue={phoneVerified && phoneConfirmationNeeded}>
                                <PreferencesConfirmation
                                    type={communicationPreferencesTypes.PHONE_NUMBER}
                                    householdId={householdId}
                                    resetCommunicationPrefCount={resetCommunicationPrefCount}
                                />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!!phoneNumber}>
                                <ConditionalRendering isTrue={!smsUnsubscribed}>
                                    <ConditionalRendering isTrue={!textMe && !phoneVerified}>
                                        <div
                                            className="gi-panel__subpanel-gray"
                                            data-testid="preferences-verify-phone-message-panel"
                                        >
                                            <div className="center-flex">
                                                <p className="mt-0 mb-0">
                                                    <FormattedMessage id="preferences_page.verify_phone_message" />
                                                </p>
                                            </div>
                                        </div>
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={textMe || (phoneVerified && smsVerified)}>
                                        <div
                                            className="gi-panel__subpanel-gray"
                                            data-testid="preferences-text-me-toggle-panel"
                                        >
                                            <UniversalInput
                                                label={{
                                                    text: 'ssap.input.label.text_me',
                                                    replacements: {
                                                        br: <br/>,
                                                        exchangeName,
                                                        privacyPolicy: (
                                                            <FormattedMessage
                                                                id="ssap.input.label.privacy_policy"
                                                                values={{
                                                                    link: (
                                                                        <a
                                                                            className="usa-link--external"
                                                                            href={PRIVACY_POLICY}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            data-testid="preferences-link-privacy-policy"
                                                                        >
                                                                            <FormattedMessage id="ssap.start.primary_contact_info.link_text" />
                                                                        </a>
                                                                    ),
                                                                }}
                                                            />
                                                        ),
                                                    },
                                                }}
                                                inputData={{
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.SWITCH_CHECKBOX,
                                                    id: 'privacyPolicy',
                                                    name: 'textMe',
                                                    classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                                    checked: textMe,
                                                    disabled: paperlessNotices && textMe && !emailMe,
                                                    tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                                    value: textMe,
                                                    currentData: {},
                                                    fields: [],
                                                }}
                                                inputActions={{
                                                    onFieldChange,
                                                }}
                                            />
                                        </div>
                                    </ConditionalRendering>
                                </ConditionalRendering>
                                <ConditionalRendering
                                    isTrue={smsUnsubscribed || (phoneVerified && !smsVerified && !textMe)}
                                >
                                    <div data-testid="preferences-sms-status-alert-container">
                                        <div
                                            style={{ backgroundPosition: '15px center' }}
                                            className="usa-alert gi-alert gi-alert__left-accent gi-alert__info"
                                            data-testid="preferences-sms-status-alert"
                                        >
                                            <div className="gi-alert__body">
                                                <p className="gi-alert__text">
                                                    {!smsUnsubscribed ? (
                                                        <>
                                                            <FormattedMessage id="preferences_page.phone_unsupported_message" />
                                                            <Button
                                                                id="preferences_verify_sms"
                                                                dataTestId="preferences-verify-sms-button"
                                                                name="verifySMS"
                                                                classes={['usa-button', 'gi-button__outline']}
                                                                type={BUTTON_INPUT_TYPES.BUTTON}
                                                                btnText={'ssap.button.verify_sms'}
                                                                handleClick={handleVerifySMSModal}
                                                            />
                                                        </>

                                                    ) : (
                                                        <span>
                                                            <FormattedMessage
                                                                id="preferences_page.phone_unsubscribed_message"
                                                                values={{
                                                                    exchangeName,
                                                                    exchangePhone:
                                                                        phoneNumberTools.formatPhoneNumber(
                                                                            exchangePhone,
                                                                        ),
                                                                    phone: phoneNumberTools.getAdjustedPhoneNumber(
                                                                        phoneNumber,
                                                                    ),
                                                                    b: function (chunks) {
                                                                        return <strong>{chunks}</strong>;
                                                                    },
                                                                    br: <br />,
                                                                }}
                                                            />
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </ConditionalRendering>
                            </ConditionalRendering>
                        </div>
                    </div>

                    <div className="contact-section my-4" data-testid="preferences-subsection-email">
                        <div className="contact-section__label p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.email" />
                            </span>
                            <Tooltip
                                id="preference_email"
                                index="preference_email"
                                content="preferences.tooltip.email"
                                icon="question-circle"
                                iconLabel={intlApi.formatMessage({ id: 'ssap.input.label.email' })}
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="contact-section__info">
                            <div className="contact-section__content gap-4">
                                <ConditionalRendering isTrue={!!emailAddress}>
                                    <span className="text-break" data-testid="preferences-email">{emailAddress}</span>
                                    <span
                                        data-testid="preferences-email-verification-status"
                                        className={`app-status-indicator font-weight-normal badge py-2 px-3 flex-shrink-0 ${emailVerified ? 'category_status--completed' : 'category_status--pending'}`}
                                    >
                                        {emailVerified ? (
                                            <>
                                                <FontAwesomeIcon icon="check-circle" className="mr-2" />
                                                <FormattedMessage id="common.status.verified" />
                                            </>
                                        ) : (
                                            intlApi
                                                .formatMessage({ id: 'common.status.unverified', defaultMessage: ' ' })
                                                .trim()
                                        )}
                                    </span>
                                </ConditionalRendering>
                                <div className="d-flex flex-wrap gap-2">
                                    <ConditionalRendering isTrue={!userRecordExists}>
                                        <Button
                                            id="preferences_email_button"
                                            dataTestId="preferences-email-button"
                                            name="emailAddress"
                                            classes={['usa-button', 'gi-button__outline']}
                                            outerClass="flex-shrink-0"
                                            type={BUTTON_INPUT_TYPES.BUTTON}
                                            btnText={`ssap.button.${emailAddress ? 'change_email' : 'add_email'}`}
                                            icon="edit"
                                            iconPlacement="START"
                                            handleClick={emailAddress ? handleChangeEmailModal : handleAddEmailModal}
                                            disabled={userRecordExists}
                                        />
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={emailAddress && !emailVerified}>
                                        <Button
                                            id="preferences_verify_email_button"
                                            dataTestId="preferences-verify-email-button"
                                            name="emailAddress"
                                            classes={['usa-button', 'gi-button__outline']}
                                            outerClass="flex-shrink-0"
                                            type={BUTTON_INPUT_TYPES.BUTTON}
                                            btnText='ssap.button.verify_email'
                                            icon="check-circle"
                                            iconPlacement="START"
                                            handleClick={handleVerifyEmailModal}
                                        />
                                    </ConditionalRendering>
                                </div>
                            </div>
                            <ConditionalRendering isTrue={emailConfirmationNeeded}>
                                <PreferencesConfirmation
                                    type={communicationPreferencesTypes.EMAIL}
                                    householdId={householdId}
                                    resetCommunicationPrefCount={resetCommunicationPrefCount}
                                />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!!emailAddress}>
                                <div className="gi-panel__subpanel-gray" data-testid="preferences-email-me-toggle-panel">
                                    <UniversalInput
                                        label={{
                                            text: 'ssap.input.label.email_me',
                                        }}
                                        inputData={{
                                            id: 'sendImportantAlerts',
                                            isInputRequired: false,
                                            type: INPUT_TYPES.SWITCH_CHECKBOX,
                                            name: 'emailMe',
                                            classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                            checked: emailMe,
                                            disabled: paperlessNotices && emailMe && !textMe,
                                            tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                            value: emailMe,
                                            currentData: {},
                                            fields: [],
                                        }}
                                        inputActions={{
                                            onFieldChange,
                                        }}
                                    />
                                </div>
                            </ConditionalRendering>
                        </div>
                    </div>

                    {showNoticeOptOut ? (
                        <div className="contact-section my-4" data-testid="preferences-subsection-marketing">
                            <div className="contact-section__label p-2">
                                <FormattedMessage id="ssap.input.label.marketing_communications" />
                            </div>
                            <div className="contact-section__info gap-4">
                                <div className="gi-panel__subpanel-gray" data-testid="preferences-marketing-opt-out-panel">
                                    <UniversalInput
                                        label={{
                                            text: 'ssap.input.label.block_marketing_communications',
                                        }}
                                        inputData={{
                                            id: 'federalCommsOnlyPreference',
                                            isInputRequired: false,
                                            type: INPUT_TYPES.SWITCH_CHECKBOX,
                                            name: 'noticeOptOut',
                                            classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                            checked: noticeOptOut,
                                            value: noticeOptOut,
                                            currentData: {},
                                            fields: [],
                                        }}
                                        inputActions={{
                                            onFieldChange,
                                        }}
                                    />
                                </div>
                                <ConditionalRendering isTrue={marketCommunConfirmationNeeded}>
                                    <PreferencesConfirmation
                                        type={communicationPreferencesTypes.NOTICES}
                                        householdId={householdId}
                                        resetCommunicationPrefCount={resetCommunicationPrefCount}
                                    />
                                </ConditionalRendering>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="gi-panel" data-testid="preferences-section-mailing">
                    <h3 className="mb-4" data-testid="preferences-heading-mailing">
                        <FormattedMessage id="ssap.page_title.mailing_prefernces" />
                    </h3>
                    <p data-testid="preferences-intro-mailing">
                        <FormattedMessage
                            id="ssap.subheader.mailing_prefernces"
                            values={{
                                exchangeName,
                                link: getLinkToDashboard(
                                    <FormattedMessage id="ssap.subheader.mailing_prefernces.link" />,
                                ),
                            }}
                        />
                    </p>

                    <PreferencesPageAddressViewer
                        {...{
                            mailingAddress,
                            getLinkToDashboard,
                            handleMailingAddressExtension,
                            mailingAddressUpdateAllowed,
                        }}
                    >
                        <ConditionalRendering isTrue={addressConfirmationNeeded}>
                            <PreferencesConfirmation
                                type={communicationPreferencesTypes.ADDRESS}
                                householdId={householdId}
                                resetCommunicationPrefCount={resetCommunicationPrefCount}
                            />
                        </ConditionalRendering>
                    </PreferencesPageAddressViewer>

                    <ConditionalRendering isTrue={!!outdatedMailingAddressReturnCount || !!atRiskDate}>
                        <section
                            aria-label="Mailing Address Warnings"
                            className="mailing-address-warnings mt-3"
                            data-testid="preferences-mailing-address-warnings"
                        >
                            <AlertMessage
                                type="warning"
                                showIcon
                                icon="exclamation-triangle"
                                content={
                                    <FormattedMessage
                                        id={outdatedMailingAddressReturnCount
                                            ? 'preferences_page.mailing_address_undeliverable'
                                            : 'preferences_page.mailing_address_at_risk'}
                                    />
                                }
                            />
                        </section>
                    </ConditionalRendering>

                    <h4 className="mb-3 mt-5" data-testid="preferences-heading-paperless-delivery">
                        <FormattedMessage id="ssap.input.option.delivery_paperless" />
                    </h4>
                    <p data-testid="preferences-intro-paperless-delivery">
                        <FormattedMessage
                            id="preferences_page.warnings.notices_delivery"
                            values={{
                                strong: chunks => <strong>{chunks}</strong>,
                                exchangeName,
                            }}
                        />{' '}
                    </p>

                    <div className="contact-section my-4" data-testid="preferences-subsection-paperless-notices">
                        <div className="contact-section__label p-2">
                            <FormattedMessage id="preferences_page.warnings.notices" />
                        </div>
                        <div className="contact-section__info">
                            <div className="gi-panel__subpanel-gray" data-testid="preferences-paperless-notices-toggle-panel">
                                <UniversalInput
                                    label={{
                                        text: 'ssap.input.label.delivery_paperless',
                                    }}
                                    inputData={{
                                        id: 'goPaperlessNotices',
                                        isInputRequired: false,
                                        type: INPUT_TYPES.SWITCH_CHECKBOX,
                                        name: 'paperlessNotices',
                                        classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                        checked: paperlessNotices,
                                        disabled: !paperlessNotices && !emailMe && !textMe,
                                        tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                        value: paperlessNotices,
                                        currentData: {},
                                        fields: [],
                                        ariaLabel: `${intlApi.formatMessage({ id: 'ssap.input.label.delivery_paperless' })} - ${intlApi.formatMessage({ id: 'preferences_page.warnings.notices' })}`,
                                    }}
                                    inputActions={{
                                        onFieldChange,
                                    }}
                                />
                            </div>
                            <div className="p-4">
                                <FormattedMessage id="preferences_page.send_you_text_message" />
                            </div>
                        </div>
                    </div>

                    <div className="contact-section my-4" data-testid="preferences-subsection-paperless-1095">
                        <div className="contact-section__label p-2">
                            <FormattedMessage id="preferences_page.warnings.tax_form" />
                        </div>
                        <div className="contact-section__info">
                            <div className="gi-panel__subpanel-gray" data-testid="preferences-paperless-1095-toggle-panel">
                                <UniversalInput
                                    label={{
                                        text: 'ssap.input.label.delivery_paperless',
                                    }}
                                    inputData={{
                                        id: 'goPaperlessForm',
                                        isInputRequired: false,
                                        type: INPUT_TYPES.SWITCH_CHECKBOX,
                                        name: 'paperless1095',
                                        classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                        checked: paperless1095,
                                        disabled: !paperless1095 && !emailMe && !textMe,
                                        tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                        value: paperless1095,
                                        currentData: {},
                                        fields: [],
                                        ariaLabel: `${intlApi.formatMessage({ id: 'ssap.input.label.delivery_paperless' })} - ${intlApi.formatMessage({ id: 'preferences_page.warnings.tax_form' })}`,
                                    }}
                                    inputActions={{
                                        onFieldChange,
                                    }}
                                />
                            </div>
                            <div className="p-4">
                                <FormattedMessage
                                    id="preferences_page.warnings.tax_form_delivery"
                                    values={{ exchangeName }}
                                />
                            </div>
                        </div>
                    </div>

                    <ConditionalRendering isTrue={paperlessConfirmationNeeded}>
                        <PreferencesConfirmation
                            type={communicationPreferencesTypes.PAPERLESS}
                            householdId={householdId}
                            resetCommunicationPrefCount={resetCommunicationPrefCount}
                        />
                    </ConditionalRendering>

                    <div data-testid="preferences-paperless-delivery-validation">
                        <ValidationErrorMessage
                            isVisible={isFormSubmitted && !isFormOnTopLevelValid}
                            messageText={VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL}
                            errorClasses={['margin-l-20', 'margin-b-10']}
                        />
                    </div>
                </div>

                <div className="gi-panel overflow-visible" data-testid="preferences-section-language">
                    <h3 className="mb-4" data-testid="preferences-heading-language">
                        <FormattedMessage id="common.language" />
                    </h3>
                    <p data-testid="preferences-intro-language">
                        <FormattedMessage
                            id="preferences_page.warnings.tell_about_language"
                            values={{
                                br: <br />,
                                exchangeName,
                                exchangePhone: exchangePhone?.replace(PHONE_NUMBER_PLUS_ONE_REGEX, ''),
                            }}
                        />
                    </p>

                    <div className="contact-section my-4" data-testid="preferences-subsection-spoken-language">
                        <div className="contact-section__label p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.preferred_spoken_language" />
                            </span>
                            <Tooltip
                                id="preferences_language_spoken"
                                index="preferences_language_spoken"
                                content="preferences.tooltip.language"
                                icon="question-circle"
                                iconLabel={intlApi.formatMessage({ id: 'ssap.input.label.preferred_spoken_language' })}
                                triggerDecorationClassName="border-none"
                                contentReplacement={{
                                    exchangePhoneNumber: exchangePhone.replace(PHONE_NUMBER_PLUS_ONE_REGEX, ''),
                                }}
                            />
                        </div>
                        <div className="contact-section__info">
                            <div className="contact-section__content gap-4">
                                <span className="text-break" data-testid="preferences-spoken-language-display">
                                    <FormattedMessage id={getLanguageFromList(languages.languageSpokenList, preferredSpokenLanguage)} />
                                </span>
                                <LanguageMenu
                                    data={data}
                                    onFieldChange={onLanguageChange}
                                    spokenOrWrittenLanguage="preferredSpokenLanguage"
                                    languageOptions={languages.languageSpokenList ?? []}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="contact-section my-4" data-testid="preferences-subsection-written-language">
                        <div className="contact-section__label p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.preferred_written_language" />
                            </span>
                            <Tooltip
                                id="preferences_language_written"
                                index="preferences_language_written"
                                content="preferences.tooltip.language"
                                icon="question-circle"
                                iconLabel={intlApi.formatMessage({ id: 'ssap.input.label.preferred_written_language' })}
                                triggerDecorationClassName="border-none"
                                contentReplacement={{
                                    exchangePhoneNumber: exchangePhone.replace(PHONE_NUMBER_PLUS_ONE_REGEX, ''),
                                }}
                            />
                        </div>
                        <div className="contact-section__info">
                            <div className="contact-section__content gap-4">
                                <span className="text-break" data-testid="preferences-written-language-display">
                                    <FormattedMessage id={getLanguageFromList(languages.languageWrittenList, preferredWrittenLanguage)} />
                                </span>
                                <LanguageMenu
                                    data={data}
                                    onFieldChange={onLanguageChange}
                                    spokenOrWrittenLanguage="preferredWrittenLanguage"
                                    languageOptions={languages.languageWrittenList ?? []}
                                />
                            </div>
                        </div>
                    </div>
                    <ConditionalRendering isTrue={languageConfirmationNeeded}>
                        <PreferencesConfirmation
                            type={communicationPreferencesTypes.LANGUAGE}
                            householdId={householdId}
                            resetCommunicationPrefCount={resetCommunicationPrefCount}
                        />
                    </ConditionalRendering>
                </div>

                <div className="height-7" />

                <div data-testid="preferences-page-footer-controls">
                    <PreferencesPageControls
                        {...{
                            actionName: SUBMIT_CLICK,
                            formName: PREFERENCES_FORM,
                            handleClick,
                            landingPage: userData.landingPage,
                            role: userData.role,
                        }}
                    />
                </div>
            </Form>
        </Fragment>
    );
};

export default CommunicationPreferencesPage;